"use client";
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  Search, Filter, Plus, Grid3X3, ChevronDown, Package, BarChart3,
  Layers, Table, AlertTriangle, CheckCircle2, XCircle, Edit3Icon,
  Trash2Icon, CheckCircle, Edit3, Trash2, RefreshCcw,
} from 'lucide-react';
import ProductCard from './ProductCard';
import Pagination, { usePagination } from '../shared/Pagination';
import { useNavigate } from 'react-router-dom';
import { useProducts } from "../context/ProductsContext";
import { useCategories } from "../context/CategoryContext";
import { useSubCategories } from "../context/SubcategoryContext";
import { useModels } from "../context/ModelContext";
import {  useLocation } from 'react-router-dom';
/* ─── Helpers ─── */
const truncate = (str, words = 5) => {
  if (!str) return '';
  const w = str.split(' ');
  return w.length <= words ? str : w.slice(0, words).join(' ') + '…';
};

const truncateChars = (str, len = 20) => {
  if (!str) return '';
  return str.length <= len ? str : str.slice(0, len) + '…';
};

/* ─── Stock Badge ─── */
const StockBadge = ({ stock }) => {
  const qty = Number(stock);
  if (qty === 0)
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-600 whitespace-nowrap">
        <XCircle size={11} /> Out
      </span>
    );
  if (qty <= 10)
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-600 whitespace-nowrap">
        <AlertTriangle size={11} /> Low ({qty})
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 whitespace-nowrap">
      <CheckCircle2 size={11} /> {qty}
    </span>
  );
};

/* ─── Toggle Switch ───
   Accepts an optional `disabled` flag (with `disabledReason` for the tooltip).
   Used to prevent manually re-activating a product whose category,
   sub-category, or modal-category (model) is itself Inactive — the product
   can only truly go Active again once its parent does. */
const ToggleSwitch = ({ isActive, onToggle, productId, disabled = false, disabledReason }) => (
  <button
    onClick={() => !disabled && onToggle(productId)}
    disabled={disabled}
    title={disabled ? disabledReason : (isActive ? 'Set Inactive' : 'Set Active')}
    className={`relative inline-flex items-center w-9 h-5 rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-1 flex-shrink-0 ${
      isActive ? 'bg-emerald-500 focus:ring-emerald-400' : 'bg-gray-300 focus:ring-gray-400'
    } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
  >
    <span className={`inline-block w-3.5 h-3.5 bg-white rounded-full shadow transform transition-transform duration-200 ${
      isActive ? 'translate-x-4' : 'translate-x-0.5'
    }`} />
  </button>
);

/* ─── Action Buttons ───
   Takes the *effective* status + a disabled flag explicitly, rather than
   reading product.status directly, so table view respects the same
   category/sub-category/model cascade as the other views. */
const ActionButtons = ({ product, isActive, toggleDisabled, toggleDisabledReason, onEdit, onToggle, onDelete }) => (
  <div className="flex items-center gap-1 flex-shrink-0">
    <ToggleSwitch
      isActive={isActive}
      onToggle={onToggle}
      productId={product.id}
      disabled={toggleDisabled}
      disabledReason={toggleDisabledReason}
    />
    <button onClick={() => onEdit(product)} title="Edit"
      className="p-1.5 rounded-lg text-teal-700 hover:bg-teal-50 transition-all">
      <Edit3Icon size={14} />
    </button>
    <button onClick={() => onDelete(product.id)} title="Delete"
      className="p-1.5 rounded-lg text-red-400 hover:bg-red-50 transition-all">
      <Trash2Icon size={14} />
    </button>
  </div>
);

const PARENT_INACTIVE_REASON = "Inactive because its category, sub-category, or model is inactive";

const ProductPage = () => {
  const navigate = useNavigate();
  const { categories = [], fetchCategories, loading: categoriesLoading } = useCategories();
  const { subCategories = [], fetchSubCategories, loading: subCategoriesLoading } = useSubCategories();
  const { models = [], fetchModels, loading: modelsLoading } = useModels();
  const { products, loading, error, addProduct, toggleProductStatus, updateProduct, deleteProduct } = useProducts();

  const safeProducts = Array.isArray(products) ? products : [];

 const location = useLocation();
const [searchTerm, setSearchTerm] = useState(location.state?.prefillSearch || '');
  const [statusFilter,      setStatusFilter]       = useState('all');
  const [categoryFilter,    setCategoryFilter]     = useState('all');
  const [subCategoryFilter, setSubCategoryFilter]  = useState('all');
  const [stockFilter,       setStockFilter]        = useState('all');
  const [sortBy,            setSortBy]             = useState('name');
  const [viewMode,          setViewMode]           = useState('grid');
  const [showFilters,       setShowFilters]        = useState(false);
  const [selectedProducts,  setSelectedProducts]   = useState([]);
  const [activeDropdown,    setActiveDropdown]     = useState(null);
  const [toast,             setToast]              = useState({ visible: false, message: '', type: 'success' });

  const showToast = useCallback((message = 'Status updated successfully!', type = 'success') => {
    setToast({ visible: true, message, type });
    setTimeout(() => setToast(t => ({ ...t, visible: false })), 3000);
  }, []);

  useEffect(() => {
    fetchCategories?.().catch(e => console.warn(e));
    fetchSubCategories?.().catch(e => console.warn(e));
    fetchModels?.().catch(e => console.warn(e));
  }, [fetchCategories, fetchSubCategories, fetchModels]);

  // Resolves a product's category / sub-category / modal-category (model) ids,
  // tolerating whichever naming convention the backend used for each field.
  const getProductParentIds = (product) => ({
    catId:   product.category_id      ?? product.categoryId      ?? product.categoryID      ?? '',
    subId:   product.sub_category_id  ?? product.subcategory_id  ?? product.subCategoryId    ?? '',
    modelId: product.modal_category_id ?? product.modalCategoryId ?? product.model_id ?? product.modelId ?? '',
  });

  // A product is only effectively Active when it, its category, its
  // sub-category, AND (if set) its modal-category/model are all Active.
  // If any parent is Inactive, the product is forced Inactive regardless of
  // its own stored status.
  const getEffectiveStatus = useCallback((product) => {
    const { catId, subId, modelId } = getProductParentIds(product);
    const cat   = categories.find(c => String(c.id) === String(catId));
    const sub   = subCategories.find(s => String(s.id) === String(subId));
    const model = modelId ? models.find(m => String(m.id) === String(modelId)) : null;

    const catInactive   = cat?.status === 'Inactive' || cat?.status === 'inactive' || cat?.status === 0;
    const subInactive   = sub?.status === 'Inactive' || sub?.status === 'inactive' || sub?.status === 0;
    const modelInactive = model && (model.status === 'Inactive' || model.status === 'inactive' || model.status === 0);

    if (catInactive || subInactive || modelInactive) return 'Inactive';
    return product.status;
  }, [categories, subCategories, models]);

  // True only when the product's own stored status is Active but a parent
  // forces it Inactive — i.e. the toggle should be disabled because flipping
  // it wouldn't actually make the product active until the parent is fixed.
  const isToggleLockedInactive = (product) => {
    const rawActive = product.status === 'Active' || product.status === 'active' || product.status === 1;
    return rawActive && getEffectiveStatus(product) === 'Inactive';
  };

  const statusOptions = useMemo(() => [
    { value: 'all',      label: 'All Products', count: safeProducts.length },
    { value: 'Active',   label: 'Active',       count: safeProducts.filter(p => getEffectiveStatus(p) === 'Active').length },
    { value: 'Inactive', label: 'Inactive',     count: safeProducts.filter(p => getEffectiveStatus(p) === 'Inactive').length },
  ], [safeProducts, categories, subCategories, models, getEffectiveStatus]);

  const categoryOptions = useMemo(() => {
    const sf = statusFilter === 'all' ? safeProducts : safeProducts.filter(p => getEffectiveStatus(p) === statusFilter);
    const filtered = categories
      .map(cat => ({
        value: String(cat.id), label: cat.name,
        count: sf.filter(p => String(p.category_id ?? p.categoryId ?? p.categoryID ?? '') === String(cat.id)).length,
      }))
      .filter(o => o.count > 0);
    return [{ value: 'all', label: 'All Categories', count: sf.length }, ...filtered];
  }, [categories, safeProducts, statusFilter, subCategories, models, getEffectiveStatus]);

  const subCategoryOptions = useMemo(() => {
    const sf = statusFilter === 'all' ? safeProducts : safeProducts.filter(p => getEffectiveStatus(p) === statusFilter);
    const cf = categoryFilter === 'all' ? sf : sf.filter(p => String(p.category_id ?? p.categoryId ?? p.categoryID ?? '') === String(categoryFilter));
    const subs = categoryFilter === 'all' ? subCategories : subCategories.filter(s => String(s.category_id ?? s.categoryId ?? '') === String(categoryFilter));
    const filtered = subs
      .map(sub => ({
        value: String(sub.id), label: sub.subcat_name,
        count: cf.filter(p => String(p.sub_category_id ?? p.subcategory_id ?? p.subCategoryId ?? '') === String(sub.id)).length,
      }))
      .filter(o => o.count > 0);
    return [{ value: 'all', label: 'All Sub-Categories', count: cf.length }, ...filtered];
  }, [subCategories, safeProducts, statusFilter, categoryFilter, categories, models, getEffectiveStatus]);

  const stockOptions = [
    { value: 'all',          label: 'All Stock' },
    { value: 'in-stock',     label: 'In Stock' },
    { value: 'low-stock',    label: 'Low (≤10)' },
    { value: 'out-of-stock', label: 'Out of Stock' },
  ];

  const sortOptions = [
    { value: 'name',        label: 'Name A→Z' },
    { value: 'name-desc',   label: 'Name Z→A' },
    { value: 'price-low',   label: 'Price ↑' },
    { value: 'price-high',  label: 'Price ↓' },
    { value: 'stock-high',  label: 'Stock ↓' },
    { value: 'stock-low',   label: 'Stock ↑' },
    { value: 'rating-high', label: 'Rating ↓' },
    { value: 'newest',      label: 'Newest' },
    { value: 'oldest',      label: 'Oldest' },
  ];

  useEffect(() => {
    if (categoryFilter === 'all') return;
    if (!categoryOptions.some(o => String(o.value) === String(categoryFilter)))
      { setCategoryFilter('all'); setSubCategoryFilter('all'); }
  }, [categoryOptions, categoryFilter]);

  useEffect(() => {
    if (subCategoryFilter === 'all') return;
    if (!subCategoryOptions.some(o => String(o.value) === String(subCategoryFilter)))
      setSubCategoryFilter('all');
  }, [subCategoryOptions, subCategoryFilter]);

  const filteredAndSortedProducts = useMemo(() => {
    let filtered = safeProducts.filter(product => {
      const catId = product.category_id     ?? product.categoryId     ?? product.categoryID    ?? '';
      const subId = product.sub_category_id ?? product.subcategory_id ?? product.subCategoryId ?? '';
      const matchesSearch =
        searchTerm === '' ||
        product.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.sku?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (Array.isArray(product.tags) && product.tags.some(t => t.toLowerCase().includes(searchTerm.toLowerCase())));
      const matchesStatus      = statusFilter === 'all'      || getEffectiveStatus(product) === statusFilter;
      const matchesCategory    = categoryFilter === 'all'    || String(catId) === String(categoryFilter);
      const matchesSubCategory = subCategoryFilter === 'all' || String(subId) === String(subCategoryFilter);
      const matchesStock =
        stockFilter === 'all' ||
        (stockFilter === 'in-stock'     && Number(product.stock) > 10) ||
        (stockFilter === 'low-stock'    && Number(product.stock) > 0 && Number(product.stock) <= 10) ||
        (stockFilter === 'out-of-stock' && Number(product.stock) === 0);
      return matchesSearch && matchesStatus && matchesCategory && matchesSubCategory && matchesStock;
    });
    filtered.sort((a, b) => {
      const pA = Number(a.price) || 0, pB = Number(b.price) || 0;
      const sA = Number(a.stock) || 0, sB = Number(b.stock) || 0;
      switch (sortBy) {
        case 'name':        return (a.name || '').localeCompare(b.name || '');
        case 'name-desc':   return (b.name || '').localeCompare(a.name || '');
        case 'price-low':   return pA - pB;
        case 'price-high':  return pB - pA;
        case 'stock-high':  return sB - sA;
        case 'stock-low':   return sA - sB;
        case 'rating-high': return (Number(b.rating) || 0) - (Number(a.rating) || 0);
        case 'newest':      return Number(b.id) - Number(a.id);
        case 'oldest':      return Number(a.id) - Number(b.id);
        default:            return 0;
      }
    });
    return filtered;
  }, [safeProducts, searchTerm, statusFilter, categoryFilter, subCategoryFilter, stockFilter, sortBy, categories, subCategories, models, getEffectiveStatus]);

  const stats = useMemo(() => {
    const totalProducts      = safeProducts.length;
    const activeProducts     = safeProducts.filter(p => getEffectiveStatus(p) === 'Active').length;
    const totalValue         = safeProducts.reduce((s, p) => s + (Number(p.max_retail_price || p.price || 0) * Number(p.stock || 0)), 0);
    const lowStockProducts   = safeProducts.filter(p => Number(p.stock) > 0 && Number(p.stock) <= 10).length;
    const outOfStockProducts = safeProducts.filter(p => Number(p.stock) === 0).length;
    const averageRating      = totalProducts === 0 ? 0 : safeProducts.reduce((s, p) => s + (p.rating || 0), 0) / totalProducts;
    return { totalProducts, activeProducts, totalValue, lowStockProducts, outOfStockProducts, averageRating: averageRating.toFixed(1) };
  }, [safeProducts, getEffectiveStatus]);

  const { page, pageSize, setPage, setPageSize, totalPages, totalItems, paginated: paginatedProducts }
    = usePagination(filteredAndSortedProducts, 10);

  useEffect(() => {
    if (page > totalPages && totalPages > 0) setPage(1);
  }, [filteredAndSortedProducts.length, totalPages]);

  const handleSelectProduct = id =>
    setSelectedProducts(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const handleSelectAll = () =>
    setSelectedProducts(selectedProducts.length === filteredAndSortedProducts.length ? [] : filteredAndSortedProducts.map(p => p.id));

  const handleDeleteProduct = async id => {
    if (!window.confirm('Delete this product?')) return;
    await deleteProduct(id);
    setSelectedProducts(p => p.filter(x => x !== id));
    setActiveDropdown(null);
  };

  const handleToggleStatus = async id => {
    const product = safeProducts.find(p => p.id === id);
    if (!product) return;

    // Guard against toggling a product whose effective status is locked
    // Inactive by a parent category/sub-category/model — the click should
    // be prevented at the UI layer (disabled control), but this is a
    // defensive backstop in case handleToggleStatus is invoked directly.
    if (isToggleLockedInactive(product)) {
      showToast(PARENT_INACTIVE_REASON, 'error');
      return;
    }

    try {
      await toggleProductStatus(id, { ...product, status: product.status === 'Active' ? 'Inactive' : 'Active' });
      showToast(`Status updated to ${product.status === 'Active' ? 'Inactive' : 'Active'} successfully!`);
    } catch { showToast('Failed to update status.', 'error'); }
    setActiveDropdown(null);
  };

  const handleEditProduct = product => { navigate(`/products/edit/${product.id}`); setActiveDropdown(null); };

  const getProductImage = product => {
    if (!product) return '';
    if (Array.isArray(product.images) && product.images.length > 0) return product.images[0];
    return product.image || product.image_url || product.imageUrl || product.thumbnail || product.photo || '';
  };

  const hasFilters = searchTerm || statusFilter !== 'all' || categoryFilter !== 'all' || subCategoryFilter !== 'all' || stockFilter !== 'all';
  const clearFilters = () => { setSearchTerm(''); setStatusFilter('all'); setCategoryFilter('all'); setSubCategoryFilter('all'); setStockFilter('all'); };

  if (loading || categoriesLoading || subCategoriesLoading)
    return <div className="p-10 text-center text-gray-500">Loading…</div>;
  if (error)
    return <div className="p-10 text-center text-red-600">{error}</div>;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 py-4 sm:py-6 space-y-4">

        {/* ── KPI Cards ── */}
        <div className="grid grid-cols-3 gap-2 sm:gap-4">
          {[
            { label: 'Total', fullLabel: 'Total Products',  value: stats.totalProducts,                     icon: Package,   accent: 'border-l-teal-500',  color: 'text-teal-700',  bg: 'bg-teal-50'  },
            { label: 'Active', fullLabel: 'Active Products', value: stats.activeProducts,                   icon: BarChart3, accent: 'border-l-green-500', color: 'text-green-600', bg: 'bg-green-50' },
            { label: 'Value', fullLabel: 'Inventory Value',  value: `₹${stats.totalValue.toLocaleString()}`, icon: BarChart3, accent: 'border-l-amber-400', color: 'text-amber-600', bg: 'bg-amber-50'  },
          ].map(({ label, fullLabel, value, icon: Icon, accent, color, bg }) => (
            <div key={label} className={`bg-white rounded-xl border border-gray-100 border-l-4 ${accent} p-2.5 sm:p-4 flex items-center gap-2 sm:gap-3 shadow-sm min-w-0`}>
              <div className={`w-7 h-7 sm:w-9 sm:h-9 ${bg} rounded-lg flex items-center justify-center flex-shrink-0`}>
                <Icon className={`w-3.5 h-3.5 sm:w-4 sm:h-4 ${color}`} />
              </div>
              <div className="min-w-0">
                <p className={`text-base sm:text-xl font-bold ${color} leading-tight truncate`}>{value}</p>
                <p className="text-[10px] sm:text-xs text-gray-400 leading-tight">
                  <span className="sm:hidden">{label}</span>
                  <span className="hidden sm:inline">{fullLabel}</span>
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* ── Filter Bar ── */}
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-3 space-y-3">

          {/* Top row */}
          <div className="flex flex-wrap gap-2 items-center">
            {/* Search — full width on mobile */}
            <div className="relative w-full sm:flex-1 sm:min-w-[180px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input type="text" placeholder="Search by name, SKU…"
                value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:bg-white transition" />
            </div>

            <div className="flex items-center gap-2 w-full sm:w-auto flex-wrap">
              {/* Filter toggle */}
              <button onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-1.5 border rounded-lg px-3 py-2 text-sm transition-colors flex-shrink-0 ${
                  showFilters || hasFilters
                    ? 'bg-teal-50 border-teal-300 text-teal-700'
                    : 'bg-white border-gray-200 text-gray-600 hover:text-teal-700'
                }`}>
                <Filter className="w-4 h-4" />
                <span className="hidden sm:inline">Filters</span>
                {hasFilters && <span className="w-1.5 h-1.5 rounded-full bg-teal-500" />}
                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
              </button>

              {/* Reset */}
              {hasFilters && (
                <button onClick={clearFilters}
                  className="flex items-center gap-1 px-2.5 py-2 text-xs text-gray-500 hover:text-red-500 border border-gray-200 rounded-lg hover:border-red-200 transition-colors flex-shrink-0">
                  <RefreshCcw className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">Reset</span>
                </button>
              )}

              <div className="hidden sm:block w-px h-6 bg-gray-200 flex-shrink-0" />

              {/* View toggle */}
              <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1 flex-shrink-0">
                {[{ mode: 'grid', Icon: Grid3X3 }, { mode: 'list', Icon: Layers }, { mode: 'table', Icon: Table }].map(({ mode, Icon }) => (
                  <button key={mode} onClick={() => setViewMode(mode)}
                    className={`p-1.5 rounded-md transition-colors ${viewMode === mode ? 'bg-white text-teal-600 shadow-sm' : 'text-gray-400 hover:text-gray-600'}`}
                    title={mode.charAt(0).toUpperCase() + mode.slice(1) + ' view'}>
                    <Icon className="w-4 h-4" />
                  </button>
                ))}
              </div>

              {/* Add button */}
              <button onClick={() => navigate('/products/add')}
                className="flex items-center gap-1.5 bg-teal-600 hover:bg-teal-700 text-white px-3 sm:px-4 py-2 rounded-xl font-semibold text-xs sm:text-sm transition-colors shadow-sm flex-shrink-0">
                <Plus className="w-4 h-4" />
                <span>Add</span>
                <span className="hidden sm:inline"> Product</span>
              </button>
            </div>
          </div>

          {/* Filter dropdowns */}
          {showFilters && (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 sm:gap-3 pt-3 border-t border-gray-100">
              {[
                { label: 'Status',       value: statusFilter,      onChange: v => { setStatusFilter(v); setCategoryFilter('all'); setSubCategoryFilter('all'); }, options: statusOptions },
                { label: 'Category',     value: categoryFilter,    onChange: v => { setCategoryFilter(v); setSubCategoryFilter('all'); }, options: categoryOptions },
                { label: 'Sub-Category', value: subCategoryFilter, onChange: setSubCategoryFilter, options: subCategoryOptions },
                { label: 'Stock',        value: stockFilter,       onChange: setStockFilter, options: stockOptions },
                { label: 'Sort By',      value: sortBy,            onChange: setSortBy, options: sortOptions },
              ].map(({ label, value, onChange, options }) => (
                <div key={label}>
                  <label className="block text-[10px] sm:text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">{label}</label>
                  <select value={value} onChange={e => onChange(e.target.value)}
                    className="w-full px-2 sm:px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-teal-500">
                    {options.map(opt => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}{opt.count !== undefined ? ` (${opt.count})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          )}

          {/* Bulk actions */}
          {selectedProducts.length > 0 && (
            <div className="flex items-center justify-between pt-3 border-t border-gray-100 flex-wrap gap-2">
              <span className="text-sm text-gray-600 font-medium">{selectedProducts.length} selected</span>
              <div className="flex gap-2">
                <button className="px-3 py-1.5 bg-red-50 text-red-700 rounded-lg hover:bg-red-100 text-xs font-medium">Delete Selected</button>
                <button className="px-3 py-1.5 bg-gray-50 text-gray-700 rounded-lg hover:bg-gray-100 text-xs font-medium"
                  onClick={async () => {
                    for (const id of selectedProducts) {
                      const p = safeProducts.find(p => p.id === id);
                      if (!p) continue;
                      if (isToggleLockedInactive(p)) continue; // skip parent-locked products
                      try { await toggleProductStatus(id, { ...p, status: p.status === 'Active' ? 'Inactive' : 'Active' }); } catch (e) { console.error(e); }
                    }
                    showToast('Bulk status updated!');
                  }}>Toggle Status</button>
              </div>
            </div>
          )}
        </div>

        {/* ── Results count ── */}
        <div className="flex items-center justify-between text-xs text-gray-500">
          <span>
            Showing <span className="font-semibold text-gray-700">{filteredAndSortedProducts.length}</span> of{' '}
            <span className="font-semibold text-gray-700">{safeProducts.length}</span> products
          </span>
          {loading && <span className="text-teal-600 font-medium animate-pulse">Refreshing…</span>}
        </div>

        {/* ── Content ── */}
        <section>
          {filteredAndSortedProducts.length === 0 ? (
            <div className="bg-white border border-gray-200 rounded-2xl py-16 sm:py-20 text-center shadow-sm">
              <div className="w-14 h-14 sm:w-16 sm:h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Package className="w-7 h-7 sm:w-8 sm:h-8 text-gray-300" />
              </div>
              <h3 className="text-base sm:text-lg font-semibold text-gray-700 mb-1">No products found</h3>
              <p className="text-sm text-gray-400 mb-5 px-4">
                {hasFilters ? 'Try adjusting your search or filters.' : 'Create your first product to get started.'}
              </p>
              {hasFilters ? (
                <button onClick={clearFilters}
                  className="px-5 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-50 transition-colors">
                  Clear filters
                </button>
              ) : (
                <button onClick={() => navigate('/products/add')}
                  className="px-5 py-2.5 bg-teal-600 hover:bg-teal-700 text-white rounded-xl text-sm font-semibold transition-colors">
                  Add Product
                </button>
              )}
            </div>
          ) : (
            <>
              {/* ── GRID ── */}
              {viewMode === 'grid' && (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {paginatedProducts.map(product => (
                      <ProductCard
                        key={product.id} product={product}
                        effectiveStatus={getEffectiveStatus(product)}
                        categories={categories} subCategories={subCategories}
                        isSelected={selectedProducts.includes(product.id)}
                        onSelect={handleSelectProduct} onEdit={handleEditProduct}
                        onDelete={handleDeleteProduct} onToggleStatus={handleToggleStatus}
                        activeDropdown={activeDropdown} setActiveDropdown={setActiveDropdown}
                      />
                    ))}
                  </div>
                  {totalPages > 1 && (
                    <div className="bg-white border border-gray-200 rounded-xl px-3 sm:px-4 shadow-sm mt-4">
                      <Pagination currentPage={page} totalPages={totalPages} totalItems={totalItems}
                        pageSize={pageSize} onPageChange={setPage} onPageSizeChange={setPageSize} />
                    </div>
                  )}
                </>
              )}

              {/* ── LIST ── */}
              {viewMode === 'list' && (
                <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
                  <div className="p-3 space-y-2">
                    {/* Desktop header */}
                    <div className="hidden md:flex items-center gap-3 px-3 py-3 bg-teal-700 text-white text-xs font-semibold uppercase tracking-wider rounded-xl">
                      <span className="w-10 flex-shrink-0">Img</span>
                      <span className="w-44 flex-shrink-0">Product</span>
                      <span className="w-20 flex-shrink-0">Category</span>
                      <span className="flex-1" />
                      <span className="w-24 text-right flex-shrink-0">Price</span>
                      <span className="w-20 text-center flex-shrink-0">Stock</span>
                      <span className="w-10 text-center flex-shrink-0">On</span>
                      <span className="w-14 text-right flex-shrink-0">Actions</span>
                    </div>
                    {/* Mobile header */}
                    <div className="flex md:hidden items-center px-3 py-2.5 bg-teal-700 text-white text-xs font-semibold uppercase tracking-wider rounded-xl">
                      <span className="flex-1">Product</span>
                      <span>Status / Actions</span>
                    </div>

                    {paginatedProducts.map(product => {
                      const img      = getProductImage(product);
                      const category = categories.find(c => String(c.id) === String(product.category_id ?? product.categoryId ?? ''));
                      const isActive = getEffectiveStatus(product) === 'Active';
                      const toggleLocked = isToggleLockedInactive(product);
                      // Truncated strings
                      const productName    = truncate(product.name, 4);
                      const categoryName   = truncate(category?.name, 3);
                      const skuShort       = truncateChars(product.sku, 12);

                      return (
                        <div key={product.id}
                          className={`bg-white border border-gray-200 rounded-xl px-3 py-2.5 transition-all duration-200 ${
                            isActive ? 'hover:shadow-sm hover:border-teal-200' : 'opacity-60 grayscale'
                          }`}>

                          {/* Desktop row */}
                          <div className="hidden md:flex items-center gap-3">
                            {/* Thumbnail */}
                            <div className="w-10 h-10 rounded-xl overflow-hidden border border-gray-100 flex-shrink-0 bg-gray-100 flex items-center justify-center">
                              {img
                                ? <img src={img} alt={product.name} className="w-full h-full object-cover" onError={e => { e.target.src = 'https://placehold.co/40x40?text=?'; }} />
                                : <Package className="w-4 h-4 text-gray-300" />}
                            </div>
                            {/* Name + SKU */}
                            <div className="w-44 flex-shrink-0 min-w-0">
                              <p className="text-sm font-semibold text-gray-800 truncate" title={product.name}>{productName}</p>
                              {product.sku && <p className="text-xs text-gray-400 font-mono truncate">{skuShort}</p>}
                              {toggleLocked && (
                                <p className="text-[10px] text-red-500 font-medium truncate" title={PARENT_INACTIVE_REASON}>
                                  Parent inactive
                                </p>
                              )}
                            </div>
                            {/* Category */}
                            <div className="w-20 flex-shrink-0 min-w-0">
                              {category && (
                                <span className="text-xs text-teal-600 bg-teal-50 px-1.5 py-0.5 rounded-full truncate block text-center" title={category.name}>
                                  {categoryName}
                                </span>
                              )}
                            </div>
                            <div className="flex-1" />
                            {/* Price */}
                            <div className="w-24 text-right flex-shrink-0">
                              <p className="text-sm font-bold text-gray-800 whitespace-nowrap">₹{Number(product.price).toLocaleString('en-IN')}</p>
                              {product.max_retail_price && Number(product.max_retail_price) !== Number(product.price) && (
                                <p className="text-xs text-gray-400 line-through whitespace-nowrap">₹{Number(product.max_retail_price).toLocaleString('en-IN')}</p>
                              )}
                            </div>
                            {/* Stock */}
                            <div className="w-20 text-center flex-shrink-0">
                              <StockBadge stock={product.stock} />
                            </div>
                            {/* Toggle */}
                            <div className="w-10 flex justify-center flex-shrink-0">
                              <ToggleSwitch
                                isActive={isActive}
                                onToggle={handleToggleStatus}
                                productId={product.id}
                                disabled={toggleLocked}
                                disabledReason={PARENT_INACTIVE_REASON}
                              />
                            </div>
                            {/* Actions */}
                            <div className="w-14 flex items-center justify-end gap-1 flex-shrink-0">
                              <button onClick={() => handleEditProduct(product)}
                                className="p-1.5 rounded-lg text-teal-600 hover:bg-teal-50 transition-colors" title="Edit">
                                <Edit3 className="w-3.5 h-3.5" />
                              </button>
                              <button onClick={() => handleDeleteProduct(product.id)}
                                className="p-1.5 rounded-lg text-red-400 hover:bg-red-50 transition-colors" title="Delete">
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>

                          {/* Mobile / tablet card */}
                          <div className="flex md:hidden items-center gap-3">
                            <div className="w-12 h-12 rounded-xl overflow-hidden border border-gray-100 flex-shrink-0 bg-gray-100 flex items-center justify-center">
                              {img
                                ? <img src={img} alt={product.name} className="w-full h-full object-cover" onError={e => { e.target.src = 'https://placehold.co/48x48?text=?'; }} />
                                : <Package className="w-5 h-5 text-gray-300" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold text-gray-800 truncate" title={product.name}>{productName}</p>
                              {category && <p className="text-xs text-teal-600 truncate">{categoryName}</p>}
                              <div className="flex items-center gap-2 mt-1 flex-wrap">
                                <span className="text-xs font-bold text-gray-700 whitespace-nowrap">₹{Number(product.price).toLocaleString('en-IN')}</span>
                                <StockBadge stock={product.stock} />
                              </div>
                              {toggleLocked && (
                                <p className="text-[10px] text-red-500 font-medium mt-0.5" title={PARENT_INACTIVE_REASON}>
                                  Parent inactive
                                </p>
                              )}
                            </div>
                            <div className="flex flex-col items-end gap-2 flex-shrink-0">
                              <ToggleSwitch
                                isActive={isActive}
                                onToggle={handleToggleStatus}
                                productId={product.id}
                                disabled={toggleLocked}
                                disabledReason={PARENT_INACTIVE_REASON}
                              />
                              <div className="flex items-center gap-1">
                                <button onClick={() => handleEditProduct(product)}
                                  className="p-1.5 rounded-lg text-teal-600 hover:bg-teal-50 transition-colors" title="Edit">
                                  <Edit3 className="w-3.5 h-3.5" />
                                </button>
                                <button onClick={() => handleDeleteProduct(product.id)}
                                  className="p-1.5 rounded-lg text-red-400 hover:bg-red-50 transition-colors" title="Delete">
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="px-3 sm:px-4 border-t border-gray-100">
                    <Pagination currentPage={page} totalPages={totalPages} totalItems={totalItems}
                      pageSize={pageSize} onPageChange={setPage} onPageSizeChange={setPageSize} />
                  </div>
                </div>
              )}

              {/* ── TABLE ── */}
              {viewMode === 'table' && (
                <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-teal-700 text-white">
                          <th className="px-3 sm:px-4 py-3.5 text-left text-xs font-semibold uppercase tracking-wider w-12">Img</th>
                          <th className="px-3 sm:px-4 py-3.5 text-left text-xs font-semibold uppercase tracking-wider">Product</th>
                          <th className="px-3 sm:px-4 py-3.5 text-left text-xs font-semibold uppercase tracking-wider hidden lg:table-cell">Category</th>
                          <th className="px-3 sm:px-4 py-3.5 text-right text-xs font-semibold uppercase tracking-wider hidden sm:table-cell">Price</th>
                          <th className="px-3 sm:px-4 py-3.5 text-center text-xs font-semibold uppercase tracking-wider hidden sm:table-cell">Stock</th>
                          <th className="px-3 sm:px-4 py-3.5 text-center text-xs font-semibold uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {paginatedProducts.map((product, idx) => {
                          const img      = getProductImage(product);
                          const category = categories.find(c => String(c.id) === String(product.category_id ?? product.categoryId ?? ''));
                          const isActive = getEffectiveStatus(product) === 'Active';
                          const toggleLocked = isToggleLockedInactive(product);
                          return (
                            <tr key={product.id}
                              className={`hover:bg-teal-50/40 transition-colors ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'} ${!isActive ? 'opacity-60' : ''}`}>
                              <td className="px-3 sm:px-4 py-3">
                                <div className="w-9 h-9 rounded-xl overflow-hidden border border-gray-100 bg-gray-100 flex items-center justify-center">
                                  {img
                                    ? <img src={img} alt={product.name} className="w-full h-full object-cover" onError={e => { e.target.src = 'https://placehold.co/36x36?text=?'; }} />
                                    : <Package className="w-4 h-4 text-gray-300" />}
                                </div>
                              </td>
                              <td className="px-3 sm:px-4 py-3 max-w-[130px] sm:max-w-[180px]">
                                <p className="font-semibold text-gray-800 text-xs sm:text-sm truncate" title={product.name}>
                                  {truncate(product.name, 4)}
                                </p>
                                {product.sku && (
                                  <p className="text-xs text-gray-400 font-mono truncate mt-0.5">{truncateChars(product.sku, 12)}</p>
                                )}
                                {toggleLocked && (
                                  <p className="text-[10px] text-red-500 font-medium truncate mt-0.5" title={PARENT_INACTIVE_REASON}>
                                    Parent inactive
                                  </p>
                                )}
                                {/* Price + stock inline on mobile */}
                                <div className="flex items-center gap-2 mt-0.5 sm:hidden flex-wrap">
                                  <span className="text-xs font-bold text-gray-700 whitespace-nowrap">₹{Number(product.price).toLocaleString('en-IN')}</span>
                                  <StockBadge stock={product.stock} />
                                </div>
                                {/* Category inline below lg */}
                                {category && (
                                  <span className="lg:hidden text-xs text-teal-700 bg-teal-50 px-1.5 py-0.5 rounded-full mt-0.5 inline-block truncate max-w-[100px]" title={category.name}>
                                    {truncate(category.name, 2)}
                                  </span>
                                )}
                              </td>
                              <td className="px-3 sm:px-4 py-3 hidden lg:table-cell">
                                {category
                                  ? <span className="text-xs text-teal-700 bg-teal-50 border border-teal-100 px-2 py-1 rounded-full truncate max-w-[100px] block" title={category.name}>
                                      {truncate(category.name, 3)}
                                    </span>
                                  : <span className="text-xs text-gray-400">—</span>}
                              </td>
                              <td className="px-3 sm:px-4 py-3 text-right whitespace-nowrap hidden sm:table-cell">
                                <p className="font-semibold text-gray-800 text-sm">₹{Number(product.price).toLocaleString('en-IN')}</p>
                                {product.max_retail_price && Number(product.max_retail_price) !== Number(product.price) && (
                                  <p className="text-xs text-gray-400 line-through">₹{Number(product.max_retail_price).toLocaleString('en-IN')}</p>
                                )}
                              </td>
                              <td className="px-3 sm:px-4 py-3 text-center hidden sm:table-cell">
                                <StockBadge stock={product.stock} />
                              </td>
                              <td className="px-3 sm:px-4 py-3">
                                <div className="flex items-center justify-center">
                                  <ActionButtons
                                    product={product}
                                    isActive={isActive}
                                    toggleDisabled={toggleLocked}
                                    toggleDisabledReason={PARENT_INACTIVE_REASON}
                                    onToggle={handleToggleStatus}
                                    onEdit={handleEditProduct}
                                    onDelete={handleDeleteProduct}
                                  />
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <div className="px-3 sm:px-4 border-t border-gray-100">
                    <Pagination currentPage={page} totalPages={totalPages} totalItems={totalItems}
                      pageSize={pageSize} onPageChange={setPage} onPageSizeChange={setPageSize} />
                  </div>
                </div>
              )}
            </>
          )}
        </section>
      </div>

      {activeDropdown && <div className="fixed inset-0 z-40" onClick={() => setActiveDropdown(null)} />}

      {/* ── Toast ── */}
      <div style={{
        position: 'fixed', bottom: 16, right: 16, zIndex: 9999,
        transition: 'all 0.35s ease',
        opacity:   toast.visible ? 1 : 0,
        transform: toast.visible ? 'translateY(0) scale(1)' : 'translateY(12px) scale(0.96)',
        pointerEvents: toast.visible ? 'auto' : 'none',
      }}>
        <div className={`flex items-center gap-3 px-4 py-3 rounded-xl shadow-xl text-sm font-semibold max-w-[calc(100vw-32px)] ${
          toast.type === 'error' ? 'bg-red-600 text-white' : 'bg-teal-600 text-white'
        }`}>
          {toast.type === 'error' ? <XCircle className="w-4 h-4 flex-shrink-0" /> : <CheckCircle className="w-4 h-4 flex-shrink-0" />}
          {toast.message}
        </div>
      </div>
    </div>
  );
};

export default ProductPage;